import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase'; // Import supabase client
import { useWorkspace } from '../../context/WorkspaceContext'; // Import useWorkspace
import Button from '../../components/Button/Button'; // Assuming you have a Button component
import CurrentSubscriptionDisplay from './CurrentSubscriptionDisplay'; // Import new component
import AvailablePlansDisplay from './AvailablePlansDisplay'; // Import new component
import './PaymentManager.css'; // Import the CSS file

// New interface representing a Tier with its associated pricing
export interface TierPlan {
  tierName: string; // e.g., 'Team', 'Enterprise', 'Free'
  description?: string;
  monthlyPriceId?: string;
  monthlyDisplayPrice?: string;
  monthlyAmount?: number;
  monthlyProductId?: string; // ID of the product for the monthly plan
  yearlyPriceId?: string;
  yearlyDisplayPrice?: string;
  yearlyAmount?: number;
  yearlyProductId?: string; // ID of the product for the yearly plan
}

// Helper function to format price
const formatPrice = (price: any): string | undefined => {
  if (!price) return undefined;

  if (price.amount_type === 'free') {
    return 'Free';
  }

  if (price.price_amount === undefined || !price.price_currency || !price.recurring_interval) {
    return undefined; // Not enough info
  }

  const amount = price.price_amount / 100; // Assuming price is in cents
  const currencySymbol = price.price_currency.toUpperCase() === 'USD' ? '$' : price.price_currency;
  const interval = price.recurring_interval;

  return `${currencySymbol}${amount.toFixed(2)} / ${interval}`;
}

// Interface for Subscription data (adjust based on your table structure)
export interface Subscription {
  id: number; // Changed to number based on image
  team_id: string;
  status: string;
  product_name?: string; // Use product_name
  renewal_date?: string; // Add renewal_date (string initially from DB)
  seats?: number; // Add seats
  cancel_at_period_end?: boolean; // Added cancel flag
  // Add other relevant fields if needed
}

const PaymentManager: React.FC = () => {
  const { selectedTeam } = useWorkspace(); // Get selected team
  // Use new state for processed tier plans
  const [tierPlans, setTierPlans] = useState<TierPlan[]>([]);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(true);
  const [productsError, setProductsError] = useState<string | null>(null);

  // State for current subscription
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState<boolean>(true);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  // State for checkout process
  const [isCreatingCheckout, setIsCreatingCheckout] = useState<string | null>(null); // Store priceId being processed
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // State for portal session process
  const [isCreatingPortalSession, setIsCreatingPortalSession] = useState<boolean>(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  // State for fade-in effect
  const [startFade, setStartFade] = useState(false);

  // State for billing interval
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');

  // Combined loading state
  const isLoading = loadingProducts || loadingSubscription;

  useEffect(() => {
    const fetchData = async () => {
      // Reset states
      setLoadingProducts(true);
      setProductsError(null);
      setLoadingSubscription(true);
      setSubscriptionError(null);
      setCurrentSubscription(null); // Reset subscription on team change

      // Fetch Products (already implemented)
      try {
        const { data: productsData, error: functionError } = await supabase.functions.invoke(
          'fetch-polar-products',
          { method: 'GET' }
        );
        if (functionError) throw new Error(`Supabase function error: ${functionError.message}`);
        if (!productsData || !Array.isArray(productsData.items)) {
          console.error("Invalid product data structure received:", productsData);
          throw new Error('Received invalid data structure from function.');
        }
        
        // Group fetched items by tier
        const groupedByTier: { [key: string]: any[] } = {};
        productsData.items.forEach((item: any) => {
          const tier = item.metadata?.tier?.toLowerCase();
          if (tier) {
            if (!groupedByTier[tier]) {
              groupedByTier[tier] = [];
            }
            groupedByTier[tier].push(item);
          }
        });

        const tierOrder = ['team', 'enterprise', 'free']; // Desired order
        const tierPlansData: TierPlan[] = [];

        tierOrder.forEach(tierName => {
          const group = groupedByTier[tierName];
          if (group) {
            // Find items based on their *first price's* interval
            const monthlyItem = group.find(item => item.prices?.[0]?.recurring_interval === 'month');
            const yearlyItem = group.find(item => item.prices?.[0]?.recurring_interval === 'year');
        
            const monthlyPrice = monthlyItem?.prices?.[0];
            const yearlyPrice = yearlyItem?.prices?.[0];
        
            // Use description from monthly item, fallback to yearly
            const description = monthlyItem?.description ?? yearlyItem?.description;
        
            tierPlansData.push({
              tierName: tierName.charAt(0).toUpperCase() + tierName.slice(1), // Capitalize
              description: description,
              monthlyPriceId: monthlyPrice?.id,
              monthlyDisplayPrice: formatPrice(monthlyPrice),
              monthlyAmount: monthlyPrice?.price_amount,
              monthlyProductId: monthlyItem?.id, // Product ID for the monthly item
              yearlyPriceId: yearlyPrice?.id,
              yearlyDisplayPrice: formatPrice(yearlyPrice),
              yearlyAmount: yearlyPrice?.price_amount,
              yearlyProductId: yearlyItem?.id, // Product ID for the yearly item
            });
          }
        });
        
        console.log('Processed Tier Plans:', tierPlansData); // Log processed data
        setTierPlans(tierPlansData); // Set the new state

      } catch (err) {
        console.error("Failed to fetch Polar products via Supabase function:", err);
        const errorMsg = err instanceof Error ? err.message : 'Failed to load products due to an unknown error.';
        setProductsError(errorMsg);
      } finally {
        setLoadingProducts(false);
      }

      // Fetch Current Subscription if a team is selected
      if (selectedTeam?.id) {
        try {
          console.log(`Fetching subscription for team ID: ${selectedTeam.id}`); // Log Team ID
          const { data: subscriptionData, error: subError } = await supabase
            .from('Subscriptions') // Use correct table name
            .select('id, team_id, status, product_name, renewal_date, cancel_at_period_end') 
            .eq('team_id', selectedTeam.id)
            .maybeSingle(); // Expect zero or one relevant subscription

          if (subError) {
            console.error('Supabase subscription fetch error:', subError); // Log Supabase error
            // Don't throw if error is just "No rows found" by maybeSingle, unless you require a subscription
            if (subError.code !== 'PGRST116') { 
              throw new Error(`Database error: ${subError.message}`);
            }
            // Otherwise, no active subscription found, which is fine.
            console.log('No subscription row found (PGRST116).'); // Log no row found
          }
          
          console.log('Raw subscriptionData received:', subscriptionData); // Log raw data
          setCurrentSubscription(subscriptionData as Subscription | null);
          console.log('CurrentSubscription state set to:', subscriptionData as Subscription | null); // Log state set

        } catch (err) {
          console.error("Failed to fetch subscription data:", err); // Log catch block error
          const errorMsg = err instanceof Error ? err.message : 'Failed to load subscription details.';
          setSubscriptionError(errorMsg);
        } finally {
          setLoadingSubscription(false);
        }
      } else {
        // No team selected, so no subscription to load
        setLoadingSubscription(false);
      }
    };

    fetchData();
    // Re-run effect if selectedTeam changes
  }, [selectedTeam?.id]); 

  // Effect to trigger fade-in after loading
  useEffect(() => {
    if (!isLoading) {
      // Use a small delay to ensure the element is rendered before adding the class
      const timer = setTimeout(() => {
        setStartFade(true);
      }, 50); // Adjust delay if needed
      return () => clearTimeout(timer);
    } else {
      // Reset fade state if loading starts again
      setStartFade(false);
    }
  }, [isLoading]);

  const handlePurchase = async (priceId: string | undefined, productId: string | undefined) => {
    if (!priceId || !productId || !selectedTeam?.id) {
      console.error('Cannot purchase without priceId, productId or teamId');
      setCheckoutError('Cannot initiate purchase. Missing required information.');
      return;
    }

    setIsCreatingCheckout(priceId); // Indicate loading for this specific price
    setCheckoutError(null);

    try {
      const { data, error } = await supabase.functions.invoke(
        'create-polar-checkout',
        {
          method: 'POST',
          body: { 
            priceId: priceId, 
            productId: productId,
            teamId: selectedTeam.id 
          },
        }
      );

      if (error) throw new Error(`Function invoke error: ${error.message}`);
      if (!data?.checkoutUrl) throw new Error('Checkout URL not received from function.');

      // Redirect user to Polar checkout page
      window.location.href = data.checkoutUrl;

    } catch (err) {
      console.error('Error creating checkout session:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to start checkout process.';
      setCheckoutError(errorMsg);
    } finally {
      setIsCreatingCheckout(null); // Clear loading state
    }
  };

  // Handler for managing subscription portal
  const handleManageSubscription = async () => {
    if (!selectedTeam?.id) {
      console.error('Cannot manage subscription without teamId');
      setPortalError('Cannot manage subscription. Missing required information.');
      return;
    }

    setIsCreatingPortalSession(true);
    setPortalError(null);

    try {
      const { data, error } = await supabase.functions.invoke(
        'get-polar-portal-url',
        {
          method: 'POST',
          body: { 
            teamId: selectedTeam.id,
            returnUrl: window.location.href // Return to current page after portal session
          },
        }
      );

      if (error) throw new Error(`Function invoke error: ${error.message}`);
      if (!data?.portalUrl) throw new Error('Portal URL not received from function.');

      // Redirect user to Polar portal page
      window.location.href = data.portalUrl;

    } catch (err) {
      console.error('Error creating portal session:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to create portal session.';
      setPortalError(errorMsg);
    } finally {
      setIsCreatingPortalSession(false);
    }
  };

  return (
    <>
      {/* Unified Payment Section */}
      <div className="section">
        {/* Persistent Header */}
        <div className="section-header">
          <div className="header-title">Your plan</div>
          {/* Actions can be added here if needed later */}
          <div className="header-actions"></div> 
        </div>

        {/* Persistent Container */}
        <div className="payment-content-area">
          {/* Conditional Content Inside Container */}
          {isLoading ? (
            // Loading State
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '32px' }}>
              <Button variant="text" size="large" isLoading={true} disabled={true} />
            </div>
          ) : (
            // Content Wrapper
            <> 
              {currentSubscription ? (
                // === Render Current Subscription Details ===
                <CurrentSubscriptionDisplay 
                  subscription={currentSubscription}
                  tierPlans={tierPlans}
                  billingInterval={billingInterval} // Pass interval if needed for display
                  onManageSubscription={handleManageSubscription}
                  isCreatingPortalSession={isCreatingPortalSession}
                  subscriptionError={subscriptionError}
                  portalError={portalError}
                  startFade={startFade}
                />
              ) : (
                // === Render Available Plans ===
                <AvailablePlansDisplay 
                  tierPlans={tierPlans}
                  billingInterval={billingInterval}
                  onBillingIntervalChange={setBillingInterval} // Pass setter
                  onPurchase={handlePurchase}
                  isCreatingCheckout={isCreatingCheckout}
                  checkoutError={checkoutError}
                  productsError={productsError}
                  startFade={startFade}
                  hideFreeDebug={true}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentManager; 